// WordPress dependencies
import { useState, useEffect, useRef } from '@wordpress/element';
import { createPortal } from 'react-dom';
import { Spinner, Button } from '@wordpress/components';
import apiFetch from '@wordpress/api-fetch';
import { __ } from '@wordpress/i18n';

const CarouselPreviewTab = ( props ) => {
	const [ modalOpen, setModalOpen ] = useState( false );
	const [ previewState, setPreviewState ] = useState( {
		html: '',
		loading: false,
		error: '',
	} );
	const timerRef = useRef( null );
	const mountedRef = useRef( true );
	const prevAttributesRef = useRef( null );

	useEffect( () => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
			clearTimeout( timerRef.current );
		};
	}, [] );

	// Fetch on open (immediately), then debounce only on subsequent attribute changes.
	useEffect( () => {
		if ( ! modalOpen ) return;
		clearTimeout( timerRef.current );

		const isFirstOpen = prevAttributesRef.current === null;
		const attributesChanged = prevAttributesRef.current !== props.attributes;
		prevAttributesRef.current = props.attributes;

		const delay = isFirstOpen ? 0 : ( attributesChanged ? 400 : 0 );
		timerRef.current = setTimeout( fetchPreview, delay );
		return () => clearTimeout( timerRef.current );
	}, [ props.attributes, modalOpen ] );

	// Lock body scroll when modal is open.
	useEffect( () => {
		document.body.style.overflow = modalOpen ? 'hidden' : '';
		return () => { document.body.style.overflow = ''; };
	}, [ modalOpen ] );

	// Close on Escape key.
	useEffect( () => {
		if ( ! modalOpen ) return;
		const onKey = ( e ) => { if ( e.key === 'Escape' ) closeModal(); };
		document.addEventListener( 'keydown', onKey );
		return () => document.removeEventListener( 'keydown', onKey );
	}, [ modalOpen ] );

	function openModal() {
		prevAttributesRef.current = null;
		setModalOpen( true );
	}

	function closeModal() {
		setModalOpen( false );
		clearTimeout( timerRef.current );
	}

	function fetchPreview() {
		if ( ! mountedRef.current ) return;
		setPreviewState( ( s ) => ( { ...s, loading: true, error: '' } ) );

		apiFetch( {
			path: '/coca-bais/v1/preview-carousel',
			method: 'POST',
			data: props.attributes,
		} )
			.then( ( res ) => {
				if ( ! mountedRef.current ) return;
				setPreviewState( { html: res.html || '', loading: false, error: '' } );
			} )
			.catch( () => {
				if ( ! mountedRef.current ) return;
				setPreviewState( {
					html: '',
					loading: false,
					error: __( 'Preview failed. Please add sliders to the carousel and try again.', 'wp-before-after-image-slider' ),
				} );
			} );
	}

	function buildSrcdoc( carouselHtml ) {
		const assets = window.COCA_BAIS_PREVIEW || {};
		const common = assets.common || {};
		const types = assets.types || {};
		// Collect styles: common + all slider types (default, triple, etc.) + carousel.
		const styles = [ ...( common.styles || [] ) ];
		// Collect scripts: common first, then all slider-type scripts, then carousel last
		// (carousel.js must run after swiper is loaded, which is in types.carousel.scripts).
		const sliderScripts = [];
		const sliderStyles = [];
		Object.entries( types ).forEach( ( [ key, typeAssets ] ) => {
			if ( key !== 'carousel' ) {
				sliderStyles.push( ...( typeAssets.styles || [] ) );
				sliderScripts.push( ...( typeAssets.scripts || [] ) );
			}
		} );
		const carouselAssets = types.carousel || {};
		styles.push( ...sliderStyles, ...( carouselAssets.styles || [] ) );
		const scripts = [
			...( common.scripts || [] ),
			...sliderScripts,
			...( carouselAssets.scripts || [] ),
		];
		const styleLinks = styles
			.map( ( href ) => `<link rel="stylesheet" href="${ href }">` )
			.join( '\n' );
		const scriptTags = scripts
			.map( ( src ) => `<script src="${ src }"><\/script>` )
			.join( '\n' );

		return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
${ styleLinks }
<style>
  html, body { margin: 0; padding: 40px; background: #111; box-sizing: border-box; }
  *, *::before, *::after { box-sizing: border-box; }
  .bais-carousel-wrapper { max-width: 100%; }
  .coca-bais-shortcode { max-width: 100%; }
  .coca-bais-container, .coca-bais-triple-container, .coca-bais-caption-wrapper { margin: 0 auto; }
  .video-control-area, .coca-bais-caption-wrapper { color: #fff; }
  .components-notice { border-left: 4px solid #f0b849; background: #fff8e5; padding: 12px 16px; }
</style>
</head>
<body>
${ carouselHtml }
${ scriptTags }
</body>
</html>`;
	}

	const { html, loading, error } = previewState;

	const modal = modalOpen ? createPortal(
		<div
			className="coca-bais-preview-overlay"
			role="dialog"
			aria-modal="true"
			aria-label={ __( 'Carousel Preview', 'wp-before-after-image-slider' ) }
		>
			<div className="coca-bais-preview-backdrop" onClick={ closeModal } />

			<div className="coca-bais-preview-modal">
				<div className="coca-bais-preview-modal-header">
					<span className="coca-bais-preview-modal-title">
						{ __( 'Live Preview', 'wp-before-after-image-slider' ) }
					</span>
					<button
						type="button"
						className="coca-bais-preview-close"
						onClick={ closeModal }
						aria-label={ __( 'Close preview', 'wp-before-after-image-slider' ) }
					>
						&#x2715;
					</button>
				</div>

				<div className="coca-bais-preview-modal-body">
					{ loading && (
						<div className="coca-bais-preview-status">
							<Spinner />
							<span>{ __( 'Generating preview…', 'wp-before-after-image-slider' ) }</span>
						</div>
					) }
					{ ! loading && error && (
						<div className="coca-bais-preview-status coca-bais-preview-error">
							{ error }
						</div>
					) }
					{ ! loading && ! error && ( ! html || html.trim() === '' ) && (
						<div className="coca-bais-preview-status coca-bais-preview-empty">
							{ __( 'Add sliders to the carousel to see the live preview.', 'wp-before-after-image-slider' ) }
						</div>
					) }
					{ ! loading && ! error && html && html.trim() !== '' && (
						<iframe
							title={ __( 'Carousel Preview', 'wp-before-after-image-slider' ) }
							srcDoc={ buildSrcdoc( html ) }
							className="coca-bais-preview-iframe"
							sandbox="allow-scripts allow-same-origin"
						/>
					) }
				</div>
			</div>
		</div>,
		document.body
	) : null;

	return (
		<>
			<Button
				variant="primary"
				className="coca-bais-preview-open-btn"
				onClick={ openModal }
			>
				{ __( '⚡ Preview', 'wp-before-after-image-slider' ) }
			</Button>
			{ modal }
		</>
	);
};

export default CarouselPreviewTab;
